import React, { useCallback, useEffect, useState } from "react";
import Pagination from "../../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../../components/ui/dashboard/Table";
import {
  IncidentReport,
  IncidentReportSearchRequest,
  IncidentReportSearchResponse,
} from "../../../incident/@types";
import { useGetIncidentReportsMutation } from "../../../incident/incidentReportApiSlice";

interface IncidentAndAccidentTab {
  driverId: string;
}

const IncidentAndAccidentTab: React.FC<IncidentAndAccidentTab> = ({
  driverId,
}) => {
  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // RTK Query mutation
  const [getIncidentReport, { data: maintenanceData, isLoading }] =
    useGetIncidentReportsMutation();

  const fetchIR = useCallback(async () => {
    const requestBody: IncidentReportSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,

      driverId: driverId,
    };

    try {
      await getIncidentReport(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch incident reports:", err);
    }
  }, [currentPage, pageSize, getIncidentReport]);

  // Fetch incident reports on component mount and when filters change
  useEffect(() => {
    fetchIR();
  }, [currentPage, pageSize, fetchIR]);

  // Table columns configuration
  const columns: Column<IncidentReport>[] = [
    {
      key: "cardCode",
      header: "Report type",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "cardCode",
      header: "Severity",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "vehicle",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "maintenanceType",
      header: "Description",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "location",
      header: "Location",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "severity",
      header: "Status",
      width: "20",
      render: (value: boolean, item: IncidentReport) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Minor",
              color: "text-green-200 border border-green-200",
            };
          if (item.severity === "Critical")
            return {
              label: "Critical",
              color: "text-red-800 border border-red-800",
            };
          if (item.severity === "Bad")
            return {
              label: "Bad",
              color: "text-yellow-800 border border-yellow-800",
            };
          return {
            label: "Critical",
            color: "text-red-800 border border-red-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "dateAdded",
      header: "Date",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = maintenanceData?.data as
    | IncidentReportSearchResponse
    | undefined;

  return (
    <div className="space-y-6 bg-white rounded-[10px] p-4">
      <div className="">
        <h2 className="text-xl font-semibold text-hColor">
          Incident & accident
        </h2>
      </div>

      {/* Table */}
      <div className="overflow-hidden space-y-6">
        <Table
          data={response?.data || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="No incident reports found"
        />

        {/* Pagination */}
        {response && (
          <Pagination
            currentPage={response.currentPage}
            totalPages={response.totalPages}
            pageSize={response.pageSize}
            totalCount={response.totalCount}
            hasNextPage={response.hasNextPage}
            hasPreviousPage={response.hasPreviousPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
};

export default IncidentAndAccidentTab;
